import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, Sparkles, Share2, Users } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

const RANGES = [
  { key: 7, label: "This week" },
  { key: 30, label: "This month" },
  { key: 90, label: "Last 90 days" },
];

const MEDALS = ["🥇", "🥈", "🥉"];

export default function Leaderboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/leaderboard", { params: { days, limit: 20 } }).then((r) => setData(r.data));
  }, [days]);

  return (
    <div>
      <section className="bg-[#1E3A2F] text-[#F7F4EB] relative overflow-hidden">
        <div className="absolute inset-0 abm-kente opacity-[0.06]" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 relative">
          <div className="inline-flex items-center gap-2 abm-chip !bg-[#DDA74A]/15 !text-[#DDA74A] !border-[#DDA74A]/40">
            <Trophy className="w-3 h-3" /> Community Helpers
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">The people connecting the dots</h1>
          <p className="mt-3 text-[#A3B3AA] max-w-2xl">Recommend someone. Get someone hired. Bring the community together. We keep score so we can thank you.</p>
          <div className="mt-6 flex gap-2">
            {RANGES.map((r) => (
              <button
                key={r.key}
                data-testid={`leaderboard-range-${r.key}`}
                onClick={() => setDays(r.key)}
                className={`rounded-full px-4 h-9 text-sm font-medium border transition ${days === r.key ? "bg-[#DDA74A] text-[#1E3A2F] border-[#DDA74A]" : "border-white/20 text-[#F7F4EB] hover:bg-white/10"}`}
              >{r.label}</button>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {data && (
          <div className="grid sm:grid-cols-3 gap-3 mb-8">
            <Tile icon={<Sparkles className="w-4 h-4 text-[#DDA74A]" />} label="Recommendations made" value={data.totals?.recommendations ?? 0} />
            <Tile icon={<Users className="w-4 h-4 text-[#C05A3D]" />} label="Needs resolved" value={data.totals?.resolved_needs ?? 0} />
            <Tile icon={<Share2 className="w-4 h-4 text-[#3B5A6B]" />} label="Top helpers" value={data.top_recommenders?.length ?? 0} />
          </div>
        )}

        <div className="rounded-2xl bg-white border border-[#E2DED0] overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-5 py-3 bg-[#F7F4EB] text-xs uppercase tracking-widest text-[#5C5C5C]">
            <div className="w-8">#</div>
            <div>Member</div>
            <div>Referrals</div>
            <div className="text-right">Recs</div>
          </div>
          {(data?.top_recommenders || []).length === 0 && (
            <div className="text-center px-5 py-14 text-[#5C5C5C]">No recommendations yet in this window. Be the first to help.</div>
          )}
          {(data?.top_recommenders || []).map((row, i) => (
            <div key={row.user_id} data-testid={`leaderboard-row-${i}`} className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-5 py-4 border-t border-[#E2DED0]">
              <div className="w-8 text-lg font-display text-[#1E3A2F]">
                {i < 3 ? MEDALS[i] : i + 1}
              </div>
              <div>
                <div className="font-medium text-[#1A1A1A] flex items-center gap-2 flex-wrap">
                  {row.name}
                  {row.verification_tier && row.verification_tier !== "none" && <VerifiedBadge tier={row.verification_tier} size="sm" />}
                </div>
                <div className="text-xs text-[#5C5C5C]">Hired via them: {row.referral_hires || 0}</div>
              </div>
              <div className="text-sm text-[#5C5C5C]">{row.referral_signups || 0}</div>
              <div className="text-right font-display text-2xl text-[#C05A3D]">{row.recommendations}</div>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-[#5C5C5C] mt-8">
          <Link to="/needs" className="text-[#C05A3D] font-medium">Help someone this week →</Link>
        </p>
      </div>
    </div>
  );
}

function Tile({ icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2DED0] p-4">
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#5C5C5C]">{icon} {label}</div>
      <div className="font-display text-3xl text-[#1E3A2F] mt-1">{value}</div>
    </div>
  );
}
